/**
 * Release 2 "Structure and governance" components (docs/specs/release2-contract.md): smart boards and system boards
 * (lane A), board field policies and tag groups / curated tags (lane B), usage rights, expiry and retention (lane C).
 * Names match `components.schemas`. Enums used by core schemas (BoardKind, SmartBoardSort, RightsLicense,
 * ReleaseStatus, RightsStatus) live in common.ts, `AssetRights` in assets.ts and `RightsFilter` in search.ts.
 *
 * Each lane appends only inside its own `// --- release2/X ---` section.
 */
import { z } from 'zod';
import {
  HexColor,
  Id,
  RIGHTS_EXPIRING_DAYS,
  SmartBoardSort,
  Timestamp,
  atLeastOneField,
} from './common.js';
import { CustomFieldValueSet } from './customFields.js';
import { AssetRights } from './assets.js';
import { SearchFilters } from './search.js';

// --- release2/A --- smart boards (S3) and system boards (S4)

/** Keys a smart board definition may not carry (`include_deleted`); `board_ids` is the watched scope. */
export const SMART_BOARD_REJECTED_FILTER_KEYS = ['include_deleted'] as const;

export const SmartBoardCreate = z.object({
  library_id: Id.optional(),
  parent_board_id: Id.nullable().optional(),
  name: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  color: HexColor.optional(),
  sort_order: z.int().optional(),
  filters: SearchFilters,
  sort: SmartBoardSort.optional(),
});
export type SmartBoardCreate = z.infer<typeof SmartBoardCreate>;

export const SmartBoardDefinition = z.object({
  filters: SearchFilters,
  /** Omitted keeps the current sort; null clears it. */
  sort: SmartBoardSort.nullable().optional(),
});
export type SmartBoardDefinition = z.infer<typeof SmartBoardDefinition>;

export const SystemBoardKey = z.enum([
  'untagged',
  'incomplete',
  'unfiled',
  'needs_review',
  'expiring',
  'duplicates',
]);
export type SystemBoardKey = z.infer<typeof SystemBoardKey>;

/**
 * Preset filters per system board, in display order (the single source for api and web).
 * `duplicates` has no listing; it links to the Duplicates page.
 */
export const SYSTEM_BOARD_PRESETS: Readonly<
  Record<SystemBoardKey, { name: string; filters: z.input<typeof SearchFilters> | null; link: string | null }>
> = {
  untagged: { name: 'Untagged', filters: { untagged: true }, link: null },
  incomplete: { name: 'Incomplete', filters: { incomplete: true }, link: null },
  unfiled: { name: 'Unfiled', filters: { unfiled: true }, link: null },
  needs_review: { name: 'Needs review', filters: { approval: ['needs_review'] }, link: null },
  expiring: {
    name: 'Expiring soon',
    filters: { rights: { expiring_within_days: RIGHTS_EXPIRING_DAYS } },
    link: null,
  },
  duplicates: { name: 'Duplicates', filters: null, link: '/duplicates' },
};

export const SystemBoard = z.object({
  key: SystemBoardKey,
  name: z.string(),
  filters: SearchFilters.nullable(),
  /** Exact count over what the caller can VIEW; null for `duplicates`. */
  count: z.int().min(0).nullable(),
  link: z.string().nullable().optional(),
});
export type SystemBoard = z.infer<typeof SystemBoard>;

// --- release2/B --- board field policies (G3), tag groups and curated tag creation (G1)

export const FieldPolicy = z
  .object({
    field_id: Id,
    required: z.boolean().default(false),
    hidden: z.boolean().default(false),
    default_value: CustomFieldValueSet.nullable().optional(),
  })
  .refine((p) => !(p.required && p.hidden), {
    message: 'required and hidden are exclusive',
    path: ['hidden'],
  });
export type FieldPolicy = z.infer<typeof FieldPolicy>;

export const EffectiveFieldPolicy = z.object({
  field_id: Id,
  required: z.boolean(),
  hidden: z.boolean(),
  default_value: CustomFieldValueSet.nullable(),
  /** Board whose row applies; null = the workspace field definition. */
  source_board_id: Id.nullable(),
});
export type EffectiveFieldPolicy = z.infer<typeof EffectiveFieldPolicy>;

export const BoardFieldPolicies = z.object({
  board_id: Id,
  items: z.array(FieldPolicy),
  effective: z.array(EffectiveFieldPolicy),
});
export type BoardFieldPolicies = z.infer<typeof BoardFieldPolicies>;

export const BOARD_FIELD_POLICIES_MAX = 50;

export const BoardFieldPoliciesSet = z.object({
  items: z.array(FieldPolicy).max(BOARD_FIELD_POLICIES_MAX),
});
export type BoardFieldPoliciesSet = z.infer<typeof BoardFieldPoliciesSet>;

export const TagGroup = z.object({
  id: Id,
  workspace_id: Id,
  name: z.string().max(100),
  color: HexColor.nullable(),
  /** An asset carries at most one user tag of the group; adding one replaces the others. */
  exclusive: z.boolean(),
  sort_order: z.int(),
  tag_count: z.int().min(0),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type TagGroup = z.infer<typeof TagGroup>;

export const TagGroupCreate = z.object({
  name: z.string().min(1).max(100),
  color: HexColor.optional(),
  exclusive: z.boolean().default(false),
  sort_order: z.int().optional(),
});
export type TagGroupCreate = z.infer<typeof TagGroupCreate>;

export const TagGroupUpdate = atLeastOneField(
  z.object({
    name: z.string().min(1).max(100).optional(),
    color: HexColor.nullable().optional(),
    exclusive: z.boolean().optional(),
    sort_order: z.int().optional(),
  }),
);
export type TagGroupUpdate = z.infer<typeof TagGroupUpdate>;

export const TagGroupAssign = z.object({
  tag_ids: z
    .array(Id)
    .min(1)
    .max(500)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'tag_ids must be unique' }),
});
export type TagGroupAssign = z.infer<typeof TagGroupAssign>;

/** `everyone` = EDIT_METADATA; `editors` = MANAGE_BOARD at workspace level; `admins` = ADMIN. */
export const TagCreationMode = z.enum(['everyone', 'editors', 'admins']);
export type TagCreationMode = z.infer<typeof TagCreationMode>;

export const TagPolicy = z.object({ tag_creation: TagCreationMode });
export type TagPolicy = z.infer<typeof TagPolicy>;

export const WsFieldPolicyPayload = z.object({
  workspace_id: Id,
  board_id: Id,
});
export type WsFieldPolicyPayload = z.infer<typeof WsFieldPolicyPayload>;

export const WsTagGroupPayload = z.object({
  workspace_id: Id,
  tag_group_id: Id,
  change: z.enum(['created', 'updated', 'deleted']),
  tag_group: TagGroup.optional(),
});
export type WsTagGroupPayload = z.infer<typeof WsTagGroupPayload>;

// --- release2/C --- usage rights + expiry (C4), retention (S7b)

export const BulkAssetRights = z.object({
  asset_ids: z.array(Id).min(1).max(5000),
  rights: AssetRights,
  /** true (default) = only keys present in `rights` change; false = replace the record. */
  merge: z.boolean().default(true),
});
export type BulkAssetRights = z.infer<typeof BulkAssetRights>;

/** `flag` = badge and filter only; `archive` = also archive the asset when rights expire. */
export const RightsExpiryAction = z.enum(['flag', 'archive']);
export type RightsExpiryAction = z.infer<typeof RightsExpiryAction>;

export const RightsPolicy = z.object({ on_expiry: RightsExpiryAction });
export type RightsPolicy = z.infer<typeof RightsPolicy>;

export const RETENTION_MAX_DAYS = 36500;

export const RetentionPolicy = z
  .object({
    enabled: z.boolean(),
    action: z.literal('archive').default('archive'),
    after_upload_days: z.int().min(1).max(RETENTION_MAX_DAYS).optional(),
    after_rights_expiry_days: z.int().min(0).max(RETENTION_MAX_DAYS).optional(),
    unapproved_days: z.int().min(1).max(RETENTION_MAX_DAYS).optional(),
  })
  .refine(
    (p) =>
      !p.enabled ||
      p.after_upload_days != null ||
      p.after_rights_expiry_days != null ||
      p.unapproved_days != null,
    { message: 'an enabled retention policy needs at least one rule', path: ['enabled'] },
  );
export type RetentionPolicy = z.infer<typeof RetentionPolicy>;

/** `retention_candidates().reason` values. */
export const RETENTION_REASONS = ['after_upload', 'after_rights_expiry', 'unapproved'] as const;
export type RetentionReason = (typeof RETENTION_REASONS)[number];

export const BoardRetention = z.object({
  board_id: Id,
  /** The board's own policy; null = inherit. */
  policy: RetentionPolicy.nullable(),
  /** The policy that applies here (null when none, or the nearest one is disabled). */
  effective: RetentionPolicy.nullable(),
  source_board_id: Id.nullable(),
});
export type BoardRetention = z.infer<typeof BoardRetention>;

export const BoardRetentionSet = z.object({ policy: RetentionPolicy.nullable() });
export type BoardRetentionSet = z.infer<typeof BoardRetentionSet>;

export const RetentionPreviewRequest = z.object({
  /** Evaluate this (unsaved) policy for the board instead of its saved one; null = as if cleared. */
  policy: RetentionPolicy.nullable().optional(),
});
export type RetentionPreviewRequest = z.infer<typeof RetentionPreviewRequest>;

export const RETENTION_PREVIEW_IDS_MAX = 50;

export const RetentionPreview = z.object({
  board_id: Id,
  count: z.int().min(0),
  by_reason: z.object({
    after_upload: z.int().min(0),
    after_rights_expiry: z.int().min(0),
    unapproved: z.int().min(0),
  }),
  asset_ids: z.array(Id).max(RETENTION_PREVIEW_IDS_MAX),
});
export type RetentionPreview = z.infer<typeof RetentionPreview>;

/** Nightly batch events carry at most this many ids; `truncated` tells clients to refetch. */
export const WS_BATCH_ASSET_IDS_MAX = 500;

export const WsRightsExpiredPayload = z.object({
  workspace_id: Id,
  action: RightsExpiryAction,
  count: z.int().min(0),
  asset_ids: z.array(Id).max(WS_BATCH_ASSET_IDS_MAX),
  truncated: z.boolean(),
});
export type WsRightsExpiredPayload = z.infer<typeof WsRightsExpiredPayload>;

export const WsRetentionAppliedPayload = z.object({
  workspace_id: Id,
  count: z.int().min(0),
  asset_ids: z.array(Id).max(WS_BATCH_ASSET_IDS_MAX),
  truncated: z.boolean(),
});
export type WsRetentionAppliedPayload = z.infer<typeof WsRetentionAppliedPayload>;

// --- release2/shared (B + C) --- one event for both workspace policies; carries only the key that changed

export const WsWorkspacePolicyPayload = z.object({
  workspace_id: Id,
  tag_creation: TagCreationMode.optional(),
  rights_on_expiry: RightsExpiryAction.optional(),
});
export type WsWorkspacePolicyPayload = z.infer<typeof WsWorkspacePolicyPayload>;
